import { matchPath } from 'react-router-dom';

import { tourSelector } from '../../tours/tourTarget';

/**
 * The DOM facts the tour engine needs, kept apart from React so each is
 * testable alone.
 */

const FOCUSABLE = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled]):not([type="hidden"])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
	'[contenteditable="true"]',
].join(', ');

const CONTROL = 'input:not([type="hidden"]), textarea, select, [contenteditable="true"]';

/** Is the element drawn (not display:none or visibility:hidden, here or above)? */
export function isDisplayed(element) {
	if (!element || !element.isConnected) return false;
	const view = element.ownerDocument.defaultView;
	for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
		if (node.hidden) return false;
		const style = view.getComputedStyle(node);
		if (style.display === 'none') return false;
		if (node === element && style.visibility === 'hidden') return false;
	}
	return true;
}

/**
 * Is the element, or something it sits in, pinned to the screen: `position:
 * fixed`, or sticky to the bottom? Scrolling cannot move such an element out
 * from under a card docked over it, so a phone docks the card at the other
 * edge for it (the tab bar is the case: KPR-613).
 */
export function isPinnedToScreen(element) {
	if (!element || !element.isConnected) return false;
	const view = element.ownerDocument.defaultView;
	for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
		const style = view.getComputedStyle(node);
		if (style.position === 'fixed') return true;
		if (style.position === 'sticky' && style.bottom && style.bottom !== 'auto') return true;
	}
	return false;
}

/** The element a target name points at right now: the first one that is drawn. */
export function findTarget(name, doc = document) {
	if (!name) return null;
	for (const element of doc.querySelectorAll(tourSelector(name))) {
		if (isDisplayed(element)) return element;
	}
	return null;
}

/** The field a `type` step watches: the target itself, or the first field inside it. */
export function controlWithin(target) {
	if (!target) return null;
	if (target.matches(CONTROL)) return target;
	return target.querySelector(CONTROL);
}

/** Does the field hold something other than blanks? */
export function hasValue(control) {
	if (!control) return false;
	if (control.isContentEditable || control.getAttribute('contenteditable') === 'true') return control.textContent.trim() !== '';
	if (control.tagName === 'SELECT' && control.multiple) return Array.from(control.selectedOptions).length > 0;
	return String(control.value ?? '').trim() !== '';
}

/** Tab stops inside an element, in DOM order, the element itself first if it is one. */
export function focusStops(root) {
	if (!root) return [];
	const inside = Array.from(root.querySelectorAll(FOCUSABLE));
	const all = root.matches(FOCUSABLE) ? [root, ...inside] : inside;
	return all.filter((element) => isDisplayed(element));
}

/**
 * Does the current path match a navigate step's pattern? `/things/:id` matches
 * `/things/9` and `/things/9/`; a trailing `/*` also matches below it.
 */
export function routeMatches(pattern, pathname) {
	if (!pattern || typeof pathname !== 'string') return false;
	try {
		return Boolean(matchPath({ path: pattern, end: !pattern.endsWith('/*') }, pathname));
	} catch {
		return false;
	}
}

/** Does the person want less motion? False where matchMedia is absent. */
export function prefersReducedMotion() {
	if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
	try {
		return Boolean(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
	} catch {
		return false;
	}
}
